import { CurdDataSetConfig, Device, FormFieldConfig, FormFieldType, Position } from '../types';
import { durationToString, toDateTimeFormat } from '@mri/functions';
import { ChoiceTypes, DeviceStatus } from '@mri/enums';
import { propertyCommonField } from './property-common-field';

function badgePositionTimeClass ( element: Device )  {
  const lastPositionTime = element.interaction?.lastPositionTime
  if (!lastPositionTime) return '';

  const startTime = new Date(lastPositionTime);
  const endTime = new Date();
  const duration = Math.abs(endTime.getTime() - startTime.getTime());

  if (duration > 1000 * 60 * 5) return 'badge text-warn-900 bg-warn-50';

  return 'badge text-primary-900 bg-primary-50';
}

function badgePositionTime(element: Device){
  const lastPositionTime = element.interaction?.lastPositionTime

  if(!lastPositionTime) return '';

  const classValue = badgePositionTimeClass(element);

  const startTime = new Date(lastPositionTime);
  const endTime = new Date();
  return `<span class="${classValue}">${durationToString(Math.abs(endTime.getTime() - startTime.getTime()))}</span>`;
}


function badgeStatusClass( element: Device ): string  {
  if (element.status === DeviceStatus.ONLINE) return 'text-success-900 bg-success-50';
  if (element.status === DeviceStatus.OFFLINE) return 'text-danger-900 bg-danger-50';
  if (element.status === DeviceStatus.UNKNOWN) return 'text-warn-900 bg-warn-50';

  return 'text-secondary-900 bg-secondary-50';
}

function badgeStatusText( element: Device ): string  {
  if (element.status === DeviceStatus.ONLINE) return 'En linea';
  if (element.status === DeviceStatus.OFFLINE) return 'Fuera de linea';
  if (element.status === DeviceStatus.UNKNOWN) return 'Con conflictos';

  return 'Nunca a conectado';
}

const filterConfig :Array<FormFieldConfig> = [
  {
    key: 'uniqueId',
    label: 'Imei',
    type: FormFieldType.STRING
  },
  {
    key: 'listenerName',
    label: 'Escuchador',
    type: FormFieldType.STRING
  },
  {
    key: 'simCardId',
    label: 'Sim Card',
    type: FormFieldType.OPTIONS,
    containerClass: 'col-12 col-sm-12',
    optionsGateway: 'sim-cards'
  },
  {
    key: 'hardwareId',
    label: 'Equipo',
    type: FormFieldType.OPTIONS,
    containerClass: 'col-12 col-sm-12',
    optionsGateway: 'hardware'
  },

  {
    key: 'status',
    label: 'Estado',
    type: FormFieldType.OPTIONS_MULTIPLE,
    containerClass: 'col-12 col-sm-12',
    options: [
      {
        label: 'En linea',
        value: DeviceStatus.ONLINE,
      },
      {
        label: 'Desconectado',
        value: DeviceStatus.OFFLINE,
      },
      {
        label: 'Desconocido',
        value: DeviceStatus.UNKNOWN,
      }
    ]
  },
  {
    ...propertyCommonField,
    type: FormFieldType.OPTIONS_MULTIPLE,
  }
]

export const deviceDataSetConfig: CurdDataSetConfig<Device> = {
  entityName: 'Dispositivo',
  collectionName: 'Devices',
  gatewayUrl: 'devices',
  baseBreadcrumb: 'devices',
  form: {
    listRedirectUrl: 'console/devices/list',
    fields: [
      {
        key: 'uniqueId',
        label: 'Codigo unico',
        type: FormFieldType.STRING,
        containerClass: 'col-md-6 col-sm-6',
        optionsConfigKey: ChoiceTypes.MOBILE_TYPE
      },
      {
        key: 'hardwareType',
        label: 'Hardware',
        type: FormFieldType.OPTIONS,
        optionsGateway: 'hardware',
        containerClass: 'col-md-6 col-sm-6'
      },
      {
        key: 'simCardId',
        label: 'Sim Card',
        type: FormFieldType.OPTIONS,
        containerClass: 'col-12 col-sm-12',
        optionsGateway: 'sim-cards'
      },
      propertyCommonField
    ]
  },
  list: {
    pageConfig: {
      title: 'Lista de dispositivos',
      breadcrumb: [
        {
          title: 'Dispositivos',
          type: 'item',
          link: '/console/devices/list'
        },
        {
          title: 'Lista',
          type: 'item',
          link: '/console/devices/list',
          pageName: 'Dispositivos'
        }
      ]
    },
    table: {
      columns: [
        {
          key: 'uniqueId', label: 'Codigo unico',
          sort: {
            id: 'uniqueId',
            fieldKey: 'uniqueId',
          }
        },

        {
          key: 'hardwareName', label: 'Equito',
          getValue: ({ element }) => element.hardware?.name || 'No definido'
        },
        {
          key: 'mobileId', label: 'Placa',
          getValue: ({ element }) => element.mobile?.name || 'No definido'
        },
        {
          key: 'lastPositionTime', label: 'Ultima posición',
          getValue: ({ element }) => {
            if(!element.interaction?.lastPositionTime ) return 'Nunca';

            const body = toDateTimeFormat(element.interaction?.lastPositionTime);
            const badge = badgePositionTime(element);

            return `${body} <span class="hidden">|</span> ${badge}`;
          },
          sort: {
            id: 'lastPositionTime',
            fieldKey: 'lastPositionTime'
          }
        },
        {
          key: 'status', label: 'Conexión',
          getValue: ({ element }) => `<span class="badge ${badgeStatusClass(element)}">${badgeStatusText(element)}</span>`
        },
        {
          key: 'phoneLine', label: 'Telefono',
          getValue: ({ element }) => element.simCard?.name || 'No definido'
        },
        {
          key: 'protocol', label: 'Protocolo',
          getValue: ({ element }) => element.current?.protocol || 'No definido'
        },
        {
          key: 'listener', label: 'Escuchador',
          getValue: ({ element }) => element.listenerName || 'No definido'
        },
        {
          key: 'owner', label: 'Dueño',
          getValue: ({ element }) => element.property?.name || 'No definido'
        }
      ]
    },
    actionsKeys: ['create', 'edit', 'transfer', 'delete', 'export-xls', 'export-pdf'],
    filter: filterConfig,
    searchFilter: filterConfig,
  },
  pages: ['list', 'create', 'edit']
};
